import { Link } from "@tanstack/react-router"
import { Layers, Search, Sparkles } from "lucide-react"
import { useMemo, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useSkill, useSkillPresets, useSkills } from "@/api/queries/skills"
import { Markdown } from "@/components/app/markdown"
import { EmptyState, ErrorState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import type { AgentFormValues } from "@/features/studio/agent-form"
import { VirtualList } from "@/features/studio/virtual-list"
import { cn } from "@/lib/utils"

function SkillPreview({ id }: { id: number | null }) {
  const { t } = useTranslation()
  const skill = useSkill(id ?? undefined)
  if (id == null)
    return (
      <EmptyState
        variant="plain"
        size="sm"
        icon={Sparkles}
        title={t("studio.agents.skills.previewEmpty")}
        className="h-full"
      />
    )
  if (skill.isPending)
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-3.5" />
        <Skeleton className="h-3.5 w-4/5" />
      </div>
    )
  if (skill.isError)
    return <ErrorState variant="plain" error={skill.error} onRetry={() => void skill.refetch()} />
  return (
    <div className="space-y-3 p-4">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">{skill.data.name}</h3>
          <code className="text-muted-foreground font-mono text-xs">{skill.data.slug}</code>
        </div>
        {skill.data.description && (
          <p className="text-muted-foreground mt-1 text-xs">{skill.data.description}</p>
        )}
      </div>
      <div className="border-t pt-3">
        <Markdown size="sm">{skill.data.body || t("studio.skills.emptyBody")}</Markdown>
      </div>
    </div>
  )
}

export function SkillsTab() {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<AgentFormValues>()
  const selected = useWatch({ control, name: "skill_ids" })
  const skills = useSkills()
  const presets = useSkillPresets()
  const [q, setQ] = useState("")
  const [focus, setFocus] = useState<number | null>(null)
  const set = (ids: number[]) => setValue("skill_ids", ids, { shouldDirty: true })

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const all = skills.data ?? []
    const filtered = needle
      ? all.filter((s) => `${s.name} ${s.slug} ${s.description}`.toLowerCase().includes(needle))
      : all
    return [...filtered].sort(
      (a, b) =>
        Number(selected.includes(b.id)) - Number(selected.includes(a.id)) || a.name.localeCompare(b.name),
    )
  }, [skills.data, q, selected])

  const applyPreset = (id: number) => {
    const p = presets.data?.find((x) => x.id === id)
    if (p) set([...new Set([...selected, ...p.skill_ids])])
  }

  return (
    <div className="grid max-w-5xl gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">{t("studio.agents.skills.title")}</h2>
          <p className="text-muted-foreground text-xs">{t("studio.agents.skills.description")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(presets.data?.length ?? 0) > 0 && (
            <Select value={null} onValueChange={(v) => v != null && applyPreset(Number(v))}>
              <SelectTrigger
                size="sm"
                className="h-8 w-44"
                aria-label={t("studio.agents.skills.applyPreset")}
              >
                <Layers className="text-muted-foreground size-3.5" />
                <SelectValue placeholder={t("studio.agents.skills.applyPreset")} />
              </SelectTrigger>
              <SelectContent>
                {presets.data?.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}
                    <span className="text-muted-foreground ml-auto text-xs tabular-nums">
                      {p.skill_ids.length}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            nativeButton={false}
            render={<Link to="/studio/skills" />}
          >
            {t("studio.agents.skills.manage")}
          </Button>
        </div>
      </div>

      {skills.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : skills.isError ? (
        <ErrorState error={skills.error} onRetry={() => void skills.refetch()} />
      ) : skills.data.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title={t("studio.agents.skills.empty.title")}
          description={t("studio.agents.skills.empty.description")}
          action={
            <Button size="sm" nativeButton={false} render={<Link to="/studio/skills" />}>
              {t("studio.agents.skills.empty.action")}
            </Button>
          }
        />
      ) : (
        <div className="bg-card grid overflow-hidden rounded-xl border @2xl:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col border-b @2xl:border-r @2xl:border-b-0">
            <div className="flex items-center gap-2 border-b p-2">
              <InputGroup className="h-8">
                <InputGroupAddon>
                  <Search />
                </InputGroupAddon>
                <InputGroupInput
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("studio.agents.skills.search")}
                  aria-label={t("studio.agents.skills.search")}
                />
              </InputGroup>
              <span className="text-muted-foreground shrink-0 px-1 text-xs tabular-nums">
                {t("studio.agents.skills.selectedCount", { count: selected.length })}
              </span>
            </div>
            <VirtualList
              items={list}
              getKey={(s) => s.id}
              maxHeight={480}
              estimateSize={56}
              className="p-1"
              aria-label={t("studio.agents.skills.title")}
              renderItem={(s) => {
                const on = selected.includes(s.id)
                return (
                  <div
                    className={cn(
                      "hover:bg-muted/50 flex items-start gap-2.5 rounded-md px-2 py-2",
                      focus === s.id && "bg-muted",
                    )}
                  >
                    <Checkbox
                      checked={on}
                      onCheckedChange={(v) =>
                        set(v ? [...selected, s.id] : selected.filter((x) => x !== s.id))
                      }
                      aria-label={s.name}
                      className="mt-0.5"
                    />
                    <button
                      type="button"
                      onClick={() => setFocus(s.id)}
                      className="min-w-0 flex-1 text-left outline-none focus-visible:underline"
                    >
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium">{s.name}</span>
                        {!s.enabled && (
                          <StatusBadge tone="warning">{t("studio.skills.disabled")}</StatusBadge>
                        )}
                      </span>
                      <span className="text-muted-foreground line-clamp-1 text-xs">
                        {s.description || s.slug}
                      </span>
                    </button>
                  </div>
                )
              }}
            />
            {list.length === 0 && (
              <p className="text-muted-foreground p-4 text-center text-sm">
                {t("table.noResultsHint", { query: q })}
              </p>
            )}
          </div>
          <div className="max-h-[540px] min-h-48 min-w-0 overflow-y-auto">
            <SkillPreview id={focus} />
          </div>
        </div>
      )}
    </div>
  )
}
